
import { useState, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Calculator, Download } from "lucide-react";
import { ApplyNowModal } from "@/components/common/ApplyNowModal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

export const EMIWidget = () => {
  const [loanAmount, setLoanAmount] = useState([500000]);
  const [tenure, setTenure] = useState([24]);
  const [rate, setRate] = useState([12]);
  
  const calculateEMI = () => {
    const P = loanAmount[0];
    const r = rate[0] / 12 / 100;
    const n = tenure[0];
    
    if (r === 0) {
      return Math.round(P / n);
    }
    
    const emi = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    return Math.round(emi);
  };

  const emi = calculateEMI();
  const totalAmount = emi * tenure[0];
  const totalInterest = totalAmount - loanAmount[0];

  const emiRef = useRef<HTMLDivElement>(null);

  const generatePDF = async () => {
    if (!emiRef.current) return;
    
    // Create a temporary div to hold our PDF content
    const content = document.createElement('div');
    content.style.fontFamily = 'Arial, sans-serif';
    content.style.padding = '20px';
    content.style.maxWidth = '800px';
    content.style.margin = '0 auto';
    content.style.color = '#333';
    content.style.fontSize = '12px';
    content.style.lineHeight = '1.5';
    
    // Add company header with logo
    content.innerHTML = `
      <div style="background: linear-gradient(135deg, #1e3a8a 0%, #1e40af 100%); padding: 20px; border-radius: 8px; margin-bottom: 20px; color: white;">
        <div style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center;">
            <img src="/images/white-pearl-finance-logo.png" alt="White Pearl Finance" style="height: 40px; margin-right: 15px;" />
            <div>
              <h1 style="font-size: 24px; margin: 0 0 5px 0; color: white;">White Pearl Finance</h1>
              <p style="margin: 0; font-size: 12px; opacity: 0.9;">Your Trusted Financial Partner</p>
            </div>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0; font-size: 12px; color: white;">${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
          </div>
        </div>
      </div>
      
      <div style="text-align: center; margin-bottom: 25px;">
        <h2 style="font-size: 20px; color: #1e40af; margin: 0 0 5px 0;">EMI Calculation</h2>
        <p style="margin: 0; color: #4b5563; font-size: 14px;">Here's your personalized EMI calculation</p>
      </div>
      
      <div style="background: #f9fafb; border-radius: 8px; padding: 20px; margin-bottom: 25px; border: 1px solid #e5e7eb;">
        <div style="display: flex; justify-content: space-between; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px dashed #d1d5db;">
          <div>
            <p style="margin: 0 0 5px 0; color: #6b7280; font-size: 12px;">Loan Amount</p>
            <p style="margin: 0; font-size: 18px; font-weight: 600; color: #1f2937;">₹${loanAmount[0].toLocaleString('en-IN')}</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0 0 5px 0; color: #6b7280; font-size: 12px;">Interest Rate</p>
            <p style="margin: 0; font-size: 18px; font-weight: 600; color: #1f2937;">${rate[0]}% p.a.</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0 0 5px 0; color: #6b7280; font-size: 12px;">Loan Tenure</p>
            <p style="margin: 0; font-size: 18px; font-weight: 600; color: #1f2937;">${tenure[0]} months</p>
          </div>
        </div>
        
        <div style="display: flex; justify-content: space-between; margin-top: 20px;">
          <div>
            <p style="margin: 0 0 5px 0; color: #6b7280; font-size: 12px;">Monthly EMI</p>
            <p style="margin: 0; font-size: 24px; font-weight: 700; color: #1e40af;">₹${emi.toLocaleString('en-IN')}</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0 0 5px 0; color: #6b7280; font-size: 12px;">Total Interest</p>
            <p style="margin: 0; font-size: 16px; font-weight: 600; color: #1f2937;">₹${totalInterest.toLocaleString('en-IN')}</p>
          </div>
          <div style="text-align: right;">
            <p style="margin: 0 0 5px 0; color: #6b7280; font-size: 12px;">Total Payment</p>
            <p style="margin: 0; font-size: 16px; font-weight: 600; color: #1f2937;">₹${totalAmount.toLocaleString('en-IN')}</p>
          </div>
        </div>
      </div>
      
      <div style="background: #f8fafc; border-radius: 8px; padding: 15px; margin-bottom: 25px; border: 1px solid #e2e8f0;">
        <h3 style="font-size: 14px; color: #1e40af; margin: 0 0 10px 0; padding-bottom: 5px; border-bottom: 1px solid #e2e8f0;">Loan Summary</h3>
        <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
          <span style="color: #4b5563;">Principal Amount</span>
          <span style="font-weight: 500;">₹${loanAmount[0].toLocaleString('en-IN')}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 10px;">
          <span style="color: #4b5563;">Interest Amount</span>
          <span style="font-weight: 500;">₹${totalInterest.toLocaleString('en-IN')}</span>
        </div>
        <div style="display: flex; justify-content: space-between; margin-bottom: 10px; padding-top: 10px; border-top: 1px dashed #d1d5db;">
          <span style="color: #1f2937; font-weight: 600;">Total Amount Payable</span>
          <span style="color: #1e40af; font-weight: 700;">₹${totalAmount.toLocaleString('en-IN')}</span>
        </div>
      </div>
      
      <div style="text-align: center; font-size: 10px; color: #6b7280; margin-top: 30px; padding-top: 15px; border-top: 1px solid #e5e7eb;">
        <p style="margin: 0 0 5px 0;">This is a computer-generated document. No signature is required.</p>
        <p style="margin: 0 0 5px 0;">White Pearl Finance | www.whitepearlfinance.com | contact@whitepearlfinance.com</p>
        <p style="margin: 0; font-style: italic;">Note: The EMI amount is indicative and may vary based on loan approval.</p>
      </div>
    `;
    
    // Add the content to the body temporarily
    document.body.appendChild(content);
    
    try {
      // Generate PDF
      const canvas = await html2canvas(content, {
        scale: 2,
        useCORS: true,
        logging: false,
        allowTaint: true
      } as any); // Using type assertion for the entire options object
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 10, 10, pdfWidth, pdfHeight);
      pdf.save(`EMI_Calculation_${new Date().toISOString().split('T')[0]}.pdf`);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      // Fallback to simple text download if PDF generation fails
      const blob = new Blob([content.innerText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'emi-calculation.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      // Clean up
      document.body.removeChild(content);
    }
  };

  return (
    <section className="py-4 sm:py-6 w-full px-1">
      <div className="mx-auto w-full max-w-[95vw] sm:max-w-2xl lg:max-w-4xl px-1 sm:px-2">
        <div className="text-center mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
            EMI Calculator
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 max-w-[90%] mx-auto">
            Calculate your monthly EMI instantly
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
          {/* Calculator Inputs */}
          <Card className="bg-slate-800/50 backdrop-blur-sm border-slate-700 w-full overflow-hidden">
            <CardHeader className="p-3 sm:p-4">
              <CardTitle className="text-lg sm:text-xl text-white flex items-center">
                <Calculator className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-blue-400 flex-shrink-0" />
                <span className="truncate">Loan Details</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-6 space-y-4 sm:space-y-6">
              <div className="space-y-1.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
                  <Label className="text-xs sm:text-sm text-white whitespace-nowrap">Loan Amount</Label>
                  <div className="relative w-full sm:w-32">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-300 text-sm">₹</span>
                    <Input
                      type="number"
                      value={loanAmount[0]}
                      onChange={(e) => setLoanAmount([Number(e.target.value)])}
                      className="pl-6 pr-2 py-1.5 h-9 sm:h-10 text-sm bg-slate-700/50 border-slate-600 text-white w-full"
                    />
                  </div>
                </div>
                <div className="px-1">
                  <Slider
                    value={loanAmount}
                    onValueChange={setLoanAmount}
                    max={5000000}
                    min={10000}
                    step={10000}
                    className="mt-2"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>₹10K</span>
                    <span>₹50L</span>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
                  <Label className="text-xs sm:text-sm text-white whitespace-nowrap">Tenure (Months)</Label>
                  <Input
                    type="number"
                    value={tenure[0]}
                    onChange={(e) => setTenure([Number(e.target.value)])}
                    className="w-full sm:w-20 py-1.5 h-9 sm:h-10 text-sm bg-slate-700/50 border-slate-600 text-white text-right"
                  />
                </div>
                <div className="px-1">
                  <Slider
                    value={tenure}
                    onValueChange={setTenure}
                    max={360}
                    min={6}
                    step={6}
                    className="mt-2"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>6 mo</span>
                    <span>30 yr</span>
                  </div>
                </div>
              </div>

              <div className="space-y-1.5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-2">
                  <Label className="text-xs sm:text-sm text-white whitespace-nowrap">Interest Rate (% p.a.)</Label>
                  <Input
                    type="number"
                    value={rate[0]}
                    onChange={(e) => setRate([Number(e.target.value)])}
                    className="w-full sm:w-20 py-1.5 h-9 sm:h-10 text-sm bg-slate-700/50 border-slate-600 text-white text-right"
                  />
                </div>
                <div className="px-1">
                  <Slider
                    value={rate}
                    onValueChange={setRate}
                    max={30}
                    min={6}
                    step={0.25}
                    className="mt-2"
                  />
                  <div className="flex justify-between text-xs text-slate-400 mt-1">
                    <span>6%</span>
                    <span>30%</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Results */}
          <Card className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur-sm border-slate-700 w-full overflow-hidden">
            <CardHeader className="p-3 sm:p-4">
              <CardTitle className="text-lg sm:text-xl text-white">
                EMI Breakdown
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2 sm:p-4 space-y-3 sm:space-y-4">
              <div className="grid grid-cols-2 gap-2">
                <div className="text-center p-1.5 sm:p-2 bg-slate-700/30 rounded-lg">
                  <div className="text-base sm:text-lg font-bold text-blue-400 whitespace-nowrap overflow-hidden text-ellipsis px-1">₹{emi.toLocaleString()}</div>
                  <div className="text-[11px] sm:text-xs text-slate-300">Monthly EMI</div>
                </div>
                <div className="text-center p-1.5 sm:p-2 bg-slate-700/30 rounded-lg">
                  <div className="text-base sm:text-lg font-bold text-green-400 whitespace-nowrap overflow-hidden text-ellipsis px-1">₹{totalAmount.toLocaleString()}</div>
                  <div className="text-[11px] sm:text-xs text-slate-300">Total Amount</div>
                </div>
              </div>

              <div className="text-center p-1.5 sm:p-2 bg-slate-700/30 rounded-lg">
                <div className="text-lg sm:text-xl font-bold text-yellow-400 px-1">₹{totalInterest.toLocaleString()}</div>
                <div className="text-[11px] sm:text-xs text-slate-300">Total Interest</div>
              </div>

              <div className="space-y-1.5 text-xs">
                <div className="flex justify-between text-white">
                  <span className="text-slate-300">Principal:</span>
                  <span className="font-medium text-right">₹{loanAmount[0].toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-white">
                  <span className="text-slate-300">Interest:</span>
                  <span className="font-medium text-right">₹{totalInterest.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-white font-bold text-xs sm:text-sm border-t border-slate-600 pt-1.5">
                  <span>Total Payable:</span>
                  <span className="text-blue-300">₹{totalAmount.toLocaleString()}</span>
                </div>
              </div>

              <div ref={emiRef} className="hidden">
                <div className="p-6 max-w-4xl mx-auto">
                  <div className="flex items-center justify-between border-b border-gray-200 pb-4 mb-6">
                    <div>
                      <h1 className="text-2xl font-bold text-gray-800">White Pearl Finance</h1>
                      <p className="text-gray-600">EMI Calculation Report</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">{new Date().toLocaleDateString()}</p>
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">Loan Summary</h2>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Loan Amount:</span>
                        <span className="font-medium">₹{loanAmount[0].toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Interest Rate:</span>
                        <span className="font-medium">{rate[0]}% per annum</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">Loan Tenure:</span>
                        <span className="font-medium">{tenure[0]} months ({(tenure[0]/12).toFixed(1)} years)</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-2 mb-4">EMI Breakdown</h2>
                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                        <div className="flex justify-between font-medium text-gray-700">
                          <span>Description</span>
                          <span>Amount (₹)</span>
                        </div>
                      </div>
                      <div className="divide-y divide-gray-200">
                        <div className="px-4 py-3 flex justify-between">
                          <span>Monthly EMI</span>
                          <span className="font-medium">₹{emi.toLocaleString()}</span>
                        </div>
                        <div className="px-4 py-3 flex justify-between bg-gray-50">
                          <span>Principal Amount</span>
                          <span>₹{loanAmount[0].toLocaleString()}</span>
                        </div>
                        <div className="px-4 py-3 flex justify-between">
                          <span>Total Interest</span>
                          <span>₹{totalInterest.toLocaleString()}</span>
                        </div>
                        <div className="px-4 py-3 flex justify-between bg-green-50 border-t-2 border-green-100">
                          <span className="font-medium">Total Payment</span>
                          <span className="font-medium text-green-700">₹{totalAmount.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-xs text-gray-500 text-center border-t border-gray-200 pt-4">
                    <p className="mb-1">This is a computer-generated document. No signature is required.</p>
                    <p>White Pearl Finance | www.whitepearlfinance.com | contact@whitepearlfinance.com</p>
                    <p className="mt-1">Note: The EMI amount is indicative and may vary based on loan approval.</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-1 gap-2 pt-2">
                <ApplyNowModal>
                  <Button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-xs sm:text-sm py-1.5 h-9 sm:h-10">
                    Apply for this Loan
                  </Button>
                </ApplyNowModal>
                <Button 
                  variant="outline" 
                  className="w-full border-blue-500 text-blue-400 hover:bg-blue-500/10 text-xs sm:text-sm py-1.5 h-9 sm:h-10"
                  onClick={generatePDF}
                >
                  <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5" />
                  <span>Download Report</span>

                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default EMIWidget;
